<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

// Shared rich renderer for a WhatsApp template — mirrors what the customer saw
// (header / body / footer / buttons). Used by the timeline bubble, the send
// form, and the picker preview, so it stays presentational (no message
// context). Colours are theme tokens throughout: on the light-green card they
// read dark, on the dark-green card they read off-white — no fixed hex.

const props = defineProps({
  viewModel: {
    type: Object,
    required: true,
  },
  // Hide the "قالب · name" eyebrow where the surrounding UI already labels the
  // template (picker card / send form header).
  showTag: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

const MEDIA_ICON = {
  image: 'i-lucide-image',
  video: 'i-lucide-video',
  document: 'i-lucide-file',
};
const mediaLabel = type =>
  ({
    image: t('WHATSAPP_TEMPLATES.PREVIEW.MEDIA_IMAGE'),
    video: t('WHATSAPP_TEMPLATES.PREVIEW.MEDIA_VIDEO'),
    document: t('WHATSAPP_TEMPLATES.PREVIEW.MEDIA_DOCUMENT'),
  })[type] || t('WHATSAPP_TEMPLATES.PREVIEW.MEDIA_DOCUMENT');
const mediaIcon = type => MEDIA_ICON[type] || 'i-lucide-file';

const header = computed(() => props.viewModel?.header || null);
const buttons = computed(() => props.viewModel?.buttons || []);
const formattedBody = computed(() =>
  props.viewModel?.body
    ? new MessageFormatter(props.viewModel.body).formattedMessage
    : ''
);
const nameSuffix = computed(() =>
  props.viewModel?.name ? `· ${props.viewModel.name}` : ''
);

const isImage = computed(
  () =>
    header.value?.kind === 'media' &&
    header.value.mediaType === 'image' &&
    header.value.url
);
const isVideo = computed(
  () =>
    header.value?.kind === 'media' &&
    header.value.mediaType === 'video' &&
    header.value.url
);
const isMediaChip = computed(
  () => header.value?.kind === 'media' && !isImage.value && !isVideo.value
);
// A document header with a fetchable URL (e.g. the order invoice PDF) — render it
// openable + inline-previewable instead of a dead filename chip. Validate the scheme
// (http/https only) so a malicious template can't inject javascript:/data: URLs.
const safeHeaderUrl = computed(() => {
  const u = header.value?.url;
  if (!u) return null;
  try {
    const parsed = new URL(u, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
});
const isPdf = computed(
  () =>
    isMediaChip.value &&
    header.value?.mediaType === 'document' &&
    !!safeHeaderUrl.value
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- eyebrow -->
    <div
      v-if="showTag"
      class="inline-flex w-fit items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
    >
      <Icon icon="i-lucide-layout-template" class="size-3" />
      <span>{{ $t('WHATSAPP_TEMPLATES.PREVIEW.TAG') }}</span>
      <span v-if="nameSuffix" dir="ltr" class="text-n-slate-10">
        {{ nameSuffix }}
      </span>
    </div>

    <!-- header -->
    <template v-if="header">
      <span
        v-if="header.kind === 'text'"
        dir="auto"
        class="text-sm font-semibold text-n-slate-12"
      >
        {{ header.text }}
      </span>
      <img
        v-else-if="isImage"
        :src="header.url"
        class="max-h-48 w-full rounded-lg object-cover"
      />
      <video
        v-else-if="isVideo"
        :src="header.url"
        controls
        class="max-h-48 w-full rounded-lg"
      />
      <template v-else-if="isMediaChip">
        <!-- openable: clicking the file name opens the document in a new tab -->
        <a
          v-if="safeHeaderUrl"
          :href="safeHeaderUrl"
          target="_blank"
          rel="noreferrer noopener nofollow"
          class="inline-flex w-fit items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-3 transition-colors"
        >
          <Icon :icon="mediaIcon(header.mediaType)" class="size-4 shrink-0" />
          <span dir="auto" class="truncate max-w-[220px]">{{
            header.name || mediaLabel(header.mediaType)
          }}</span>
          <Icon icon="i-lucide-external-link" class="size-3.5 shrink-0" />
        </a>
        <!-- inline preview for PDF documents, in the same card -->
        <object
          v-if="isPdf"
          :data="`${safeHeaderUrl}#toolbar=0&navpanes=0`"
          type="application/pdf"
          class="h-64 w-full rounded-lg border border-n-alpha-2"
        >
          <a
            :href="safeHeaderUrl"
            target="_blank"
            rel="noreferrer noopener nofollow"
            class="text-sm text-n-blue-9 dark:text-n-blue-10 underline"
          >
            {{ header.name || mediaLabel(header.mediaType) }}
          </a>
        </object>
        <!-- document without a (safe) URL: keep the plain (non-clickable) label -->
        <div
          v-else-if="!safeHeaderUrl"
          class="inline-flex w-fit items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-11"
        >
          <Icon :icon="mediaIcon(header.mediaType)" class="size-4 shrink-0" />
          <span dir="auto">{{
            header.name || mediaLabel(header.mediaType)
          }}</span>
        </div>
      </template>
    </template>

    <!-- body -->
    <span
      v-if="formattedBody"
      v-dompurify-html="formattedBody"
      dir="auto"
      class="prose prose-bubble text-sm text-n-slate-12"
    />

    <!-- footer -->
    <span v-if="viewModel.footer" dir="auto" class="text-xs text-n-slate-11">
      {{ viewModel.footer }}
    </span>

    <!-- buttons: transparent, hair-line dividers, theme-aware blue text -->
    <div
      v-if="buttons.length"
      class="mt-2 flex flex-col overflow-hidden rounded-lg border border-n-alpha-2"
    >
      <div
        v-for="(button, i) in buttons"
        :key="`btn-${i}`"
        class="flex items-center justify-center gap-1.5 bg-transparent px-3 py-2 text-sm font-medium text-n-blue-9 dark:text-n-blue-10"
        :class="i > 0 ? 'border-t border-n-alpha-2' : ''"
      >
        <Icon
          v-if="button.icon"
          :icon="button.icon"
          class="size-3.5 shrink-0"
        />
        <span class="truncate" dir="auto">{{ button.label }}</span>
      </div>
    </div>

    <!-- partial note -->
    <span v-if="viewModel.isPartial" class="text-[11px] text-n-slate-10">
      {{ $t('WHATSAPP_TEMPLATES.PREVIEW.PARTIAL') }}
    </span>
  </div>
</template>
